import Vue from 'vue'
import { v4 as uuidv4 } from 'uuid'
import downloadSessionStatus from './downloadSessionStatus'
import downloadStrategy from './downloadStrategy'

// Download configuration from environment
const BATCH_SIZE = parseInt(process.env.VUE_APP_DOWNLOAD_BATCH_SIZE) || 50
const EMAIL_LIMIT = parseInt(process.env.VUE_APP_DOWNLOAD_EMAIL_LIMIT) || 500

// Strategy thresholds (DIRECT_LIMIT correlates with BATCH_SIZE)
const DIRECT_LIMIT = BATCH_SIZE

// Retry configuration for 502 errors
const RETRY_DELAYS = [2000, 4000, 12000, 12000] // 2s, 4s, 12s, 12s

function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
}

function is502Error(error) {
    return error?.response?.status === 502 ||
           error?.status === 502 ||
           (typeof error?.message === 'string' && error.message.includes('502'))
}

/**
 * Wraps a function with retry logic for 502 errors
 * @param {Function} fn - Async function to execute
 * @returns {Promise} - Result of fn or throws after all retries exhausted
 */
async function withRetry(fn) {
    let lastError

    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        try {
            return await fn()
        } catch (error) {
            lastError = error

            if (!is502Error(error)) {
                throw error
            }

            if (attempt < RETRY_DELAYS.length) {
                await sleep(RETRY_DELAYS[attempt])
            }
        }
    }

    throw lastError
}

/**
 * Returns a user-friendly error message or null for generic errors.
 * Returning null allows the UI component to use translated fallback message.
 */
function getUserFriendlyError() {
    return null
}

const state = {
    sessions: [],
    batchSize: BATCH_SIZE,
    // Email modal state
    emailRequest: null,  // { name, count, blocked, resolve, reject }
}

export const getters = {
    sessions: state => state.sessions,
    activeSessions: state => state.sessions.filter(s =>
        s.status === downloadSessionStatus.pending ||
        s.status === downloadSessionStatus.inProgress
    ),
    hasActiveDownload: (state, getters) => getters.activeSessions.length > 0,
    batchSize: state => state.batchSize,
    sessionSummary: state => {
        return state.sessions.reduce((acc, session) => {
            acc.totalFiles += session.totalFiles
            acc.downloadedFiles += session.downloadedFiles
            return acc
        }, { totalFiles: 0, downloadedFiles: 0 })
    },
    emailRequest: state => state.emailRequest,

    /**
     * Determines download strategy based on count, limits, and available callbacks.
     * Logic: from strictest requirement to fallback, email as last resort before blocking
     */
    getStrategy: (state, getters, rootState, rootGetters) => (count, callbacks = {}) => {
        const isLimited = rootGetters['features/isLimitedResourcesEnabled']
        const canDirect = typeof callbacks.onDirect === 'function'
        const canBatch = typeof callbacks.onBatch === 'function'
        const canEmail = typeof callbacks.onEmail === 'function' && !isLimited

        // Above EMAIL_LIMIT: requires email (or blocked)
        if (count > EMAIL_LIMIT) {
            if (canEmail) return downloadStrategy.email
            return downloadStrategy.blocked
        }

        // Above DIRECT_LIMIT: batch preferred, email as fallback
        if (count > DIRECT_LIMIT) {
            if (canBatch) return downloadStrategy.batch
            if (canEmail) return downloadStrategy.email
            return downloadStrategy.blocked
        }

        // Within DIRECT_LIMIT: direct preferred, batch or email as fallback
        if (canDirect) return downloadStrategy.direct
        if (canBatch) return downloadStrategy.batch
        if (canEmail) return downloadStrategy.email
        return downloadStrategy.blocked
    },

    /**
     * Returns limits for UI display
     */
    limits: () => ({
        directLimit: DIRECT_LIMIT,
        emailLimit: EMAIL_LIMIT
    })
}

export const mutations = {
    createSession(state, session) {
        state.sessions.push(session)
    },
    updateSession(state, { id, ...updates }) {
        const index = state.sessions.findIndex(s => s.id === id)
        if (index === -1) return
        Vue.set(state.sessions, index, { ...state.sessions[index], ...updates })
    },
    removeSession(state, id) {
        state.sessions = state.sessions.filter(s => s.id !== id)
    },
    // Email modal mutations
    setEmailRequest(state, request) {
        state.emailRequest = request
    },
    resolveEmailRequest(state, email) {
        if (state.emailRequest?.resolve) {
            state.emailRequest.resolve(email)
        }
        state.emailRequest = null
    },
    rejectEmailRequest(state) {
        if (state.emailRequest?.reject) {
            state.emailRequest.reject(new Error('User cancelled'))
        }
        state.emailRequest = null
    }
}

export const actions = {
    /**
     * Main download action. Decides strategy based on count and available callbacks.
     * Handles iteration for batch downloads internally.
     *
     * @param {string} name - Display name for UI
     * @param {number} count - Total file count
     * @param {Function} [onDirect] - Direct download: async () => void
     * @param {Function} [onBatch] - Batch download: async (page, limit) => downloadedCount
     * @param {Function} [onEmail] - Email download: async (email) => void
     */
    async download({ state, getters, commit }, { name, count, onDirect, onBatch, onEmail }) {
        // Get strategy considering count, limits, and available callbacks
        const strategy = getters.getStrategy(count, { onDirect, onBatch, onEmail })

        // Execute based on final strategy
        switch (strategy) {
            case downloadStrategy.blocked:
                commit('setEmailRequest', { name, count, blocked: true })
                return {
                    success: false,
                    strategy,
                    error: `Cannot download more than ${EMAIL_LIMIT} files in limited resources mode`
                }

            case downloadStrategy.direct:
                return executeDirectDownload({ commit }, { name, count, onDirect })

            case downloadStrategy.email:
                try {
                    const email = await new Promise((resolve, reject) => {
                        commit('setEmailRequest', { name, count, blocked: false, resolve, reject })
                    })
                    await onEmail(email)
                    return { success: true, strategy }
                } catch (error) {
                    return { success: false, strategy, cancelled: true }
                }

            case downloadStrategy.batch:
                return executeBatchDownload({ state, commit }, { name, count, onBatch })

            default:
                return { success: false, error: 'Unknown strategy' }
        }
    },

    cancelDownload({ commit, state }, sessionId) {
        const session = state.sessions.find(s => s.id === sessionId)
        if (session) {
            commit('updateSession', {
                id: sessionId,
                cancelled: true,
                status: downloadSessionStatus.cancelled
            })
        }
    },

    removeSession({ commit }, sessionId) {
        commit('removeSession', sessionId)
    },

    clearCompletedSessions({ commit, state }) {
        state.sessions
            .filter(s => s.status !== downloadSessionStatus.inProgress &&
                         s.status !== downloadSessionStatus.pending)
            .forEach(s => commit('removeSession', s.id))
    }
}

/**
 * Execute direct download with session UI (no pagination, but shows progress)
 * Runs in background - returns immediately after creating session
 */
function executeDirectDownload({ commit }, { name, count, onDirect }) {
    const sessionId = uuidv4()

    commit('createSession', {
        id: sessionId,
        name: name || 'Download',
        status: downloadSessionStatus.inProgress,
        totalFiles: count,
        downloadedFiles: 0,
        currentBatch: 0,
        totalBatches: 1,
        cancelled: false,
        error: null,
        createdAt: new Date()
    })

    // Execute in background with retry for 502 errors
    withRetry(() => onDirect())
        .then(() => {
            commit('removeSession', sessionId)
        })
        .catch((error) => {
            commit('updateSession', {
                id: sessionId,
                status: downloadSessionStatus.failed,
                error: getUserFriendlyError(error)
            })
        })

    // Return immediately
    return { success: true, strategy: downloadStrategy.direct }
}

/**
 * Execute batch download with progress UI
 * Runs in background - returns immediately after creating session
 */
function executeBatchDownload({ state, commit }, { name, count, onBatch }) {
    const sessionId = uuidv4()
    const batchSize = state.batchSize
    const totalBatches = Math.ceil(count / batchSize)

    commit('createSession', {
        id: sessionId,
        name: name || 'Download',
        status: downloadSessionStatus.inProgress,
        totalFiles: count,
        downloadedFiles: 0,
        currentBatch: 0,
        totalBatches,
        cancelled: false,
        error: null,
        createdAt: new Date()
    })

    // Execute batch loop in background
    const runBatches = async () => {
        try {
            for (let page = 1; page <= totalBatches; page++) {
                // Check cancellation
                const session = state.sessions.find(s => s.id === sessionId)
                if (session?.cancelled) {
                    return
                }

                commit('updateSession', {
                    id: sessionId,
                    currentBatch: page
                })

                await withRetry(() => onBatch(page, batchSize))

                // Calculate downloaded files based on completed batches
                const isLastBatch = page === totalBatches
                const filesInThisBatch = isLastBatch ? (count - (page - 1) * batchSize) : batchSize
                const downloadedTotal = (page - 1) * batchSize + filesInThisBatch

                commit('updateSession', {
                    id: sessionId,
                    downloadedFiles: downloadedTotal
                })
            }

            // Remove session when completed successfully
            commit('removeSession', sessionId)

        } catch (error) {
            commit('updateSession', {
                id: sessionId,
                status: downloadSessionStatus.failed,
                error: getUserFriendlyError(error)
            })
        }
    }

    // Start batch loop without awaiting
    runBatches()

    // Return immediately
    return { success: true, strategy: downloadStrategy.batch }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}
